import {
  Box,
  Stack,
  Text,
  Icon,
  Label,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
} from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthResponse, ServicesApiEnvHealthSegmentStats } from '@/services/api/services-api';
import { delayStatus } from '../utils';

type Impact = 'High' | 'Medium' | 'Low';

interface Recommendation {
  title: string;
  description: string;
  impact: Impact;
}

function findCategory(data: ServicesApiEnvHealthResponse, name: string) {
  return data.processing.categories.find((c) => c.name === name) ?? data.egress.categories.find((c) => c.name === name);
}

function isBehind(data: ServicesApiEnvHealthResponse, name: string): boolean {
  const cat = findCategory(data, name);
  if (cat == null) return false;
  const status = delayStatus(cat.delay_seconds, cat.pending);
  return status.color !== 'green';
}

function isBackedUp(data: ServicesApiEnvHealthResponse, name: string): boolean {
  const cat = findCategory(data, name);
  if (cat == null) return false;
  const status = delayStatus(cat.delay_seconds, cat.pending);
  return status.color === 'red';
}

function deriveRecommendations(
  data: ServicesApiEnvHealthResponse,
  segments?: ServicesApiEnvHealthSegmentStats,
): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // 1. Long-running task — suggest deprioritizing
  if (data.tasks.running != null) {
    recommendations.push({
      title: 'Deprioritize long-running task',
      description:
        'A data task is currently running and consuming processing capacity. If you have other tasks in queue, you can deprioritize the current one to free up resources.',
      impact: 'High',
    });
  }

  // 2. Unused segments — suggest archiving
  if (segments != null && segments.unused_count > 0) {
    recommendations.push({
      title: `Archive ${segments.unused_count.toLocaleString()} unused segment${segments.unused_count > 1 ? 's' : ''}`,
      description:
        'These segments are being recalculated on every profile change but are not used in any campaign, broadcast, or journey. Archiving them reduces unnecessary processing.',
      impact: segments.unused_count > 50 ? 'High' : 'Medium',
    });
  }

  // 3. Newsletter-only segments — suggest archiving
  if (segments != null && segments.newsletter_only_count > 0) {
    recommendations.push({
      title: `Archive ${segments.newsletter_only_count.toLocaleString()} newsletter-only segment${
        segments.newsletter_only_count > 1 ? 's' : ''
      }`,
      description:
        'These segments are only used for newsletters. Archiving them reduces ongoing segment recalculation and frees up processing capacity.',
      impact: 'Low',
    });
  }

  // 4. CSV imports or integrations backlogging — review imports
  if (isBackedUp(data, 'csv_imports') || isBackedUp(data, 'integrations')) {
    recommendations.push({
      title: 'Review active data imports',
      description:
        'Large CSV imports and frequent integration syncs can slow down processing across your workspace. Keep this in mind when scheduling bulk imports.',
      impact: 'Medium',
    });
  }

  // 5. Identify or events severely behind — consider throttling
  if (isBackedUp(data, 'identify') || isBackedUp(data, 'events')) {
    recommendations.push({
      title: 'Consider throttling incoming data',
      description:
        'Incoming profile updates or events are backed up. This can happen during traffic spikes. If you control the data source, consider reducing the send rate temporarily.',
      impact: 'Medium',
    });
  }

  // 6. Segments processing behind — review segment complexity
  if (isBehind(data, 'segments')) {
    recommendations.push({
      title: 'Review segment complexity',
      description:
        'Segment recalculation is falling behind. Complex segments with many conditions or large audiences take longer to process. Simplifying or archiving unused segments can help.',
      impact: 'Medium',
    });
  }

  // 7. Related updates backed up — review object-triggered campaigns
  if (isBehind(data, 'related_updates')) {
    recommendations.push({
      title: 'Review object-triggered campaigns',
      description:
        'Object relationship updates share the same processing pipeline as message delivery. Removing unused object-triggered campaigns can free up capacity for outgoing messages.',
      impact: isBackedUp(data, 'related_updates') ? 'High' : 'Medium',
    });
  }

  // 8. Webhooks backed up — review webhook endpoints
  if (isBackedUp(data, 'webhooks')) {
    recommendations.push({
      title: 'Review webhook endpoints',
      description:
        'Outbound webhooks are backed up. This is often caused by slow or unresponsive receiving endpoints. Check that your webhook URLs are responding quickly.',
      impact: 'Medium',
    });
  }

  const priority: Record<Impact, number> = { High: 0, Medium: 1, Low: 2 };
  return recommendations.sort((a, b) => priority[a.impact] - priority[b.impact]).slice(0, 3);
}

interface RecommendationsPanelProps {
  data: ServicesApiEnvHealthResponse;
  segments?: ServicesApiEnvHealthSegmentStats;
}

export function RecommendationsPanel({ data, segments }: RecommendationsPanelProps) {
  const recommendations = deriveRecommendations(data, segments);

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle text="product-h4" color="subtle">
          <InlineStack gap="050" alignBlock="center">
            <Icon name="tip" size="sm" color="bold" />
            Recommendations
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Stack gap="300">
          {recommendations.map((r) => (
            <Stack key={r.title} gap="100">
              <Text size="s" weight="semibold">
                {r.title}
              </Text>
              <Text size="xs" color="subtle">
                {r.description}
              </Text>
              <Box>
                <Label color="outline">{r.impact} impact</Label>
              </Box>
            </Stack>
          ))}
        </Stack>
      </PanelSection>
    </Panel>
  );
}
